import React from 'react';
import { useTranslation } from 'react-i18next';
import type { DashboardVensterId } from '../../services/professionalDashboardApi';

/**
 * Eén van de vier vaste vensters. Teller > 0: felle badge en de actiekaarten.
 * Teller 0: Zero-State — groene statusindicator en de lijst van wat het
 * platform live bewaakt, zodat een leeg venster rust geeft in plaats van twijfel.
 */
export const SignaalVenster: React.FC<{
  id: DashboardVensterId;
  icon: React.ComponentType<{ className?: string }>;
  badge: number;
  laden: boolean;
  children?: React.ReactNode;
}> = ({ id, icon: Icon, badge, laden, children }) => {
  const { t } = useTranslation('professionalDashboard');
  const bewaakt = t(`venster.${id}.bewaakt`, { returnObjects: true }) as string[];
  const actief = badge > 0;
  return (
    <section
      aria-labelledby={`venster-${id}`}
      className={`relative flex flex-col rounded-2xl border bg-white shadow-card transition-colors ${
        actief ? 'border-gold-300' : 'border-gray-200'
      }`}
    >
      <header className="flex items-start gap-3 px-4 pt-4 pb-3 border-b border-gray-100">
        <span className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${actief ? 'bg-gold-100' : 'bg-navy-50'}`}>
          <Icon className={`w-5 h-5 ${actief ? 'text-gold-800' : 'text-navy-900'}`} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-navy-400">{t(`venster.${id}.sub`)}</p>
          <h2 id={`venster-${id}`} className="text-sm font-semibold text-navy-900">
            {t(`venster.${id}.titel`)}
          </h2>
        </div>
        <span
          aria-label={String(badge)}
          className={`inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-full text-sm font-bold ${
            actief ? 'bg-gold-500 text-navy-900 shadow-sm ring-4 ring-gold-100' : 'bg-gray-100 text-navy-400'
          }`}
        >
          {laden ? '·' : badge}
        </span>
      </header>

      <div className="flex-1 p-3">
        {laden ? (
          <div className="space-y-2">
            <div className="h-14 rounded-lg bg-gray-50 animate-pulse" />
            <div className="h-14 rounded-lg bg-gray-50 animate-pulse" />
          </div>
        ) : actief ? (
          children
        ) : (
          <div className="px-1 py-1">
            <p className="flex items-center gap-2 text-sm font-semibold text-green-800">
              <span className="relative flex w-2.5 h-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60 animate-ping motion-reduce:animate-none" />
                <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-green-500" />
              </span>
              {t('systeemActief')} · {t(`venster.${id}.leegStatus`)}
            </p>
            <p className="mt-2 text-xs leading-relaxed text-navy-500">{t(`venster.${id}.leegTekst`)}</p>
            {Array.isArray(bewaakt) && (
              <div className="mt-3">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-navy-400">{t('liveBewaakt')}</p>
                <ul className="mt-1 space-y-0.5">
                  {bewaakt.map((b) => (
                    <li key={b} className="flex items-center gap-2 text-xs text-navy-600">
                      <span className="w-1 h-1 rounded-full bg-green-500 flex-shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
